import { useState } from 'react';
import { FunctionComponent } from 'react';
import { Button } from '../../tailwindcomponents';
import { saveAs } from 'file-saver';
import { actionItem, warrantyMxiData, warrantyRequest } from '../../types/warranty';
import { CloudDownload } from '../../components/icons';
import { Column, FlowTextOptions, PdfDocument } from '../../utils/pdf/PdfDocument';
import { REPORT_MARGINS, drawReportLogo, drawReportPageChrome, getIcelandairLogo } from '../../utils/pdf/reportChrome';

type WarrantyPDFProps = {
    mxiData: warrantyMxiData[],
    warrantyRequest: warrantyRequest,
    actionItems?: actionItem[]
}

const styles: Record<string, FlowTextOptions> = {
    h1: { font: 'Times-Bold', size: 18, align: 'center' },
    h2: { font: 'Times-Bold', size: 14, marginTop: 20 },
    body: { size: 12, paddingRight: 5 },
    italic: { size: 10, paddingRight: 5 },
    underline: { size: 12, underline: true, marginTop: 10, paddingRight: 5 },
    partHeader: { font: 'Times-Bold', size: 12, marginTop: 10, marginBottom: 5, paddingRight: 5, align: 'center' },
    bold: { font: 'Times-Bold', size: 12, paddingRight: 5 },
    totalSum: { font: 'Times-Bold', size: 14, align: 'right', paddingRight: 40, marginTop: 20 },
};

const usdFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'usd',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
})

// Sums the parts together by part number (unit price x count per part number)
const sumParts = (mxiData: warrantyMxiData[]) => {
    let tempPartSum = 0;
    let checkedPartNo: string[] = [];
    mxiData.forEach(part => {
        if (!checkedPartNo.includes(part.part_no)) {
            checkedPartNo.push(part.part_no);
            let tempCount: number = mxiData.filter((tempPart) => tempPart.part_no === part.part_no).length;
            if (part !== undefined && tempCount !== undefined) {
                tempPartSum = tempPartSum + (part.unit_price * tempCount);
            }
        }
    })
    return tempPartSum;
}

// PDF content is created here, looping through the object arrays
const buildWarrantyPdf = async ({ mxiData, warrantyRequest, actionItems }: WarrantyPDFProps): Promise<Blob> => {
    const logo = await getIcelandairLogo();
    const doc = new PdfDocument({ margins: REPORT_MARGINS });
    const currentDate = new Date();
    const partSum = sumParts(mxiData);
    const totalPartSum = usdFormatter.format(partSum);
    // Labour is billed at the request's mechanic hourly rate, both in the billing rows and the final amount.
    const labourValue = warrantyRequest.actual_manhours_worked_total * Number(warrantyRequest.mechanic_hourly_rate);
    const totalSum = usdFormatter.format(partSum + labourValue + warrantyRequest.shipping_claimed_cost);

    const body = (text: string | number | null | undefined) => doc.text(text, styles.body);
    const bold = (text: string | number | null | undefined) => doc.text(text, styles.bold);
    const underline = (text: string) => doc.text(text, styles.underline);
    // Column widths are fractions of the current region plus a 15pt gutter, like the old flex layout.
    const col = (fraction: number, render: () => void): Column => ({ width: doc.width * fraction, marginRight: 15, render });
    const twoCol = (render: () => void) => col(0.5, render);
    const threeCol = (render: () => void) => col(0.33, render);
    const fourCol = (render: () => void) => col(0.22, render);

    drawReportLogo(doc, logo);
    doc.text('Warranty Claim Request', { ...styles.h1, marginBottom: 10 });

    // General info: claim number, company info, bank info etc
    doc.columns([
        twoCol(() => {
            bold(`Claim No. ${warrantyRequest.warranty_request_id}`);
            doc.text('(please refer to this number in any case of response)', styles.italic);
            body('Icelandair ehf');
            body('Reykjavik Airport - 101 Reykjavik');
            body('Email: warrantyunit@icelandair.is');
            doc.moveDown(10);
            underline('USD Bank information');
            bold('Icelandair ehf');
            bold('HSBC Bank USA N.A.');
            body('452 Fifth Avenue, New York, NY10018');
            body('FED ABA# 021001088');
            body('Chips ABA# 0108');
            body('Swift: MRMDUS33');
            body('Account: 000143545');
            doc.moveDown(10);
        }),
        twoCol(() => {
            doc.columns([
                twoCol(() => { bold('Place of issue'); body('Reykjavík'); }),
                twoCol(() => { bold('Date of issue'); body(currentDate.toLocaleDateString()); }),
            ]);
            // No right padding here: this heading was never wrapped in the original layout.
            doc.text('Accounting Department (credit and cheques)', { ...styles.underline, paddingRight: 0 });
            doc.text('VAT-ID-NO: 77630', { ...styles.body, paddingRight: 0 });
            doc.moveDown(12);
            underline('EUR Bank information');
            bold('Icelandair ehf');
            bold('Danske Bank');
            body('Holmens Kanal 2, DK-1090 Copenhagen');
            body('Denmark');
            body('Iban: DK54 3000 4073 0422 08');
            body('Swift: DABADKKK');
        }),
    ]);
    doc.moveDown(10);

    // Aircraft info for the removal
    doc.columns([
        threeCol(() => bold('Removal A/C')),
        threeCol(() => bold('Removal Date')),
        threeCol(() => bold('A/C Manufactured Date')),
    ]);
    // A request can have no MXI parts yet, so these stay blank instead of failing.
    doc.columns([
        threeCol(() => body(mxiData[0]?.aircraft)),
        threeCol(() => body(mxiData[0]?.fault_start_date_text)),
        threeCol(() => body(mxiData[0]?.aircraft_manufactured_date_text)),
    ]);

    // Part data info, one block per part
    doc.text('Warranty Claim Invoice', styles.h2);
    mxiData.forEach(part => {
        doc.keepTogether(() => {
            doc.text(part.part_description, styles.partHeader);
            doc.columns([
                twoCol(() => {
                    body(`Part Number: ${part.part_no} `);
                    body(`ATA-Chapter: ${part.ata_chapter}`);
                    body(`TT/TSN (Hours): ${part.time_since_new_flight_hours}`);
                }),
                twoCol(() => {
                    body(`Purchase Order: ${part.purchase_order_no}`);
                    body(`Serial number: ${part.part_serial_no}`);
                }),
            ]);
            doc.moveDown(10);
        });
    });

    // Reason for removal text, and also actions taken for fault
    underline('Reason for Removal');
    body(warrantyRequest.reason_for_removal);
    underline('Findings, action taken');
    actionItems?.forEach(action => body('• ' + action.action_text));
    doc.moveDown(10);

    // Billing for manhours worked on the fault
    underline('Manhours required to rectify defect');
    doc.columns([
        threeCol(() => { bold('Action Item'); body('Manhours'); body('Shipping Cost'); }),
        fourCol(() => {
            bold('Quantity');
            body(warrantyRequest.actual_manhours_worked_total_hrs + "hrs, " + warrantyRequest.actual_manhours_worked_total_min + " min");
            body('');
        }),
        fourCol(() => {
            bold('Rate/Hour');
            body(usdFormatter.format(Number(warrantyRequest.mechanic_hourly_rate)));
            body(usdFormatter.format(warrantyRequest.shipping_claimed_cost));
            bold('Total Labour');
        }),
        fourCol(() => {
            bold('Value');
            body(usdFormatter.format(labourValue));
            body(usdFormatter.format(warrantyRequest.shipping_claimed_cost));
            bold(usdFormatter.format(labourValue + warrantyRequest.shipping_claimed_cost));
        }),
    ]);
    doc.moveDown(10);

    // Billing for each part
    underline('Parts required to rectifiy defect');
    doc.columns([
        threeCol(() => bold('Part num. (serial)')),
        fourCol(() => bold('Quantity/Unit')),
        fourCol(() => bold('Value/Unit')),
        fourCol(() => bold('Value')),
    ]);
    doc.moveDown(10);
    mxiData.forEach(part => doc.columns([
        threeCol(() => body(part.part_no + ' (' + part.part_serial_no + ')')),
        fourCol(() => body('1')),
        fourCol(() => body(usdFormatter.format(part.unit_price))),
        fourCol(() => body(usdFormatter.format(part.unit_price))),
    ]));

    // Total sum for both work and parts
    doc.columns([
        threeCol(() => { }),
        fourCol(() => { }),
        fourCol(() => bold('Total parts:')),
        fourCol(() => bold(totalPartSum)),
    ]);
    doc.text(`Amount to be paid: ${totalSum}`, { ...styles.totalSum, marginBottom: 10 });

    // Page numbers and footer fixed on each page of the pdf
    drawReportPageChrome(doc, 'Icelandair | Reykjavik Airport, 101 Reykjavik | 20.10.2021', 12);
    return doc.toBlob();
};

// This component displays a download pdf button and generates the pdf on click
const WarrantyPDF: FunctionComponent<WarrantyPDFProps> = (props) => {

    const [loading, setLoading] = useState<boolean>(false);

    // Create blob with the content, that can take a while with much data. Then saved as document
    const generatePDF = async () => {
        setLoading(true);
        try {
            const blob = await buildWarrantyPdf(props);
            saveAs(blob, 'Warranty Claim Request.pdf');
        } finally {
            setLoading(false);
        }
    }
    return (
        <Button startIcon={<CloudDownload />} variant="contained" color="primary" size="medium" onClick={() => generatePDF()}>{loading === false ? "Download PDF" : "Please wait..."}</Button>
    );
};
export default WarrantyPDF;
